import React, { createContext, useContext, useReducer, ReactNode, useEffect } from 'react';
import { ForYouItem } from '../types';
import { appReducer } from './reducers';
import type { AppState, AppAction } from './appReducerTypes';
import { STORAGE_KEYS } from 'constants/storageKeys';
import { persistence } from 'services/persistence';

const initialState: AppState = {
  data: null,
  filteredEntries: [],
  currentView: 'timeline',
  filters: {
    category: '',
    provider: '',
    status: '',
    startDate: '',
    endDate: '',
    searchTerm: ''
  },
  chatJournalEntries: [],
  lastChatJournalSize: 0,
  isLoading: false,
  error: null,
  journalNotes: [],
  showJournalNotesInEHR: true,
  activePage: 'ehr',
  healthAnalysis: null,
  healthAnalysisGeneratedAt: null,
  healthAnalysisModel: null,
  healthAnalysisError: null,
  isHealthAnalysisLoading: false,
  calendarEvents: [],
  calendarSuggestions: [],
  forYouItems: []
};

interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

const loadForYouItemsFromStorage = (): ForYouItem[] =>
  persistence.getJSON<ForYouItem[]>(STORAGE_KEYS.FOR_YOU_ITEMS, []);

const saveForYouItemsToStorage = (items: ForYouItem[]): void =>
  persistence.setJSON(STORAGE_KEYS.FOR_YOU_ITEMS, items);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Initialize state with items from localStorage
  const [state, dispatch] = useReducer(appReducer, {
    ...initialState,
    forYouItems: loadForYouItemsFromStorage()
  });

  // Save items to localStorage whenever they change
  useEffect(() => {
    if (state.forYouItems.length > 0 || persistence.getItem(STORAGE_KEYS.FOR_YOU_ITEMS)) {
      saveForYouItemsToStorage(state.forYouItems);
    }
  }, [state.forYouItems]);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
